import { useState } from "react";
import { Modal, Select, Tag } from "antd5";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";

import { RouterTextLink } from "components/actions/RouterLink";
import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { useSession } from "lib/auth/authClient";
import { useSendBetterAuthInvites } from "lib/hooks/api/auth/useSendBetterAuthInvites";
import { EventNames, useTracking } from "lib/tracking";
import { toast } from "../ui/toast/Toast";

import css from "./SendInviteModal.module.scss";

type SendInviteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  linkToTeamPage?: boolean;
};

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

export default function SendInviteModal({
  isOpen,
  onClose,
  linkToTeamPage,
}: SendInviteModalProps): JSX.Element {
  const { logEvent } = useTracking();
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);

  // The invitation is created by better-auth, so inviting needs a better-auth session. A user whose
  // Rails session outlived it has none, so they can't invite until they sign in again.
  const { data: session, isPending: isSessionPending } = useSession();
  const canInvite = !!session;
  // onClose only lives in onSuccess (not after the mutation call below) so a failed send leaves
  // the modal open with the error toast visible, instead of closing over a half-sent batch.
  const { mutate: sendBetterAuthInvites } = useSendBetterAuthInvites({
    onSuccess: () => {
      toast.success("Invite sent");
      logEvent(EventNames.userInviteResent, {});
      setSaving(false);
      onClose();
    },
    onError: async (error) => {
      // Partial-batch failures throw a message naming which emails succeeded/failed (see
      // useSendBetterAuthInvites) — surface it verbatim rather than a generic string so the user
      // knows what actually happened instead of re-sending the whole list blind.
      await toast.error(
        error instanceof Error
          ? error.message
          : "Unable to send invite; please contact us if the problem persists",
      );
      setSaving(false);
    },
  });

  const handleInvitesSend = () => {
    setSaving(true);
    sendBetterAuthInvites(emails);
  };

  const invalidEmails = emails.filter((e) => !e.match(EMAIL_REGEX));

  return (
    <Modal
      open={isOpen}
      title="Invite team members"
      onCancel={onClose}
      footer={
        <div className={css.actions}>
          <Button buttonType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleInvitesSend}
            loading={saving}
            disabled={!!invalidEmails.length || isSessionPending || !canInvite}
          >
            Send invite
          </Button>
        </div>
      }
    >
      <div className={css.content}>
        {!isSessionPending && !canInvite
          ? "Your account can't send invitations yet. Please contact support and we'll add your teammates for you."
          : "Invite new users by typing in their emails below:"}
        <div className={css.emailInput}>
          <Select
            mode="tags"
            value={emails}
            onChange={(v: string[]) => setEmails(v)}
            tokenSeparators={[",", ";", " "]}
            popupClassName={css.hide}
            placeholder="email@domain.com"
            tagRender={EmailTag}
          >
            {emails?.map((email) => {
              return <Select.Option key={email}>{email}</Select.Option>;
            })}
          </Select>
          <span className={css.label}>Separate multiple email addresses with a comma</span>
          {linkToTeamPage && (
            <div className={css.teamPageLink}>
              <RouterTextLink {...appRoutes.myTeam}>
                Go to <b>Team management</b>
              </RouterTextLink>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const EmailTag = (props: CustomTagProps) => {
  const { label, value, closable, onClose } = props;

  const valid = value?.match(EMAIL_REGEX);

  return (
    <Tag
      color={valid ? "default" : "error"}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  );
};
